/**
 * ================================================================================================
 *  זיכוי — the act that REDUCES a debt, and the two it is not
 * ================================================================================================
 *
 * The Owner, 2026-11-02 and 2026-11-03:
 *
 *     *"בנה אפשרות לזיכוי מלא וחלקי, עם מסמך מקושר למקור ושימור ההיסטוריה. הפרד בין הפקת המסמך
 *      לבין ביטול עסקה או החזר לכרטיס."*
 *
 *     *"זיכוי מקטין חוב, תשלום מסלק חוב והחזר מחזיר כסף שנגבה. אין לראות בזיכוי הוכחת תשלום."*
 *
 * ── WHAT THIS CONTROL DELIBERATELY DOES NOT OFFER ─────────────────────────────────────────────
 *
 * **A REFUND.** There is no "return the money to the card" here, and that absence is the Owner's
 * separation made visible: returning money is Cardcom's `RefundByTransactionId`, it depends on
 * whether the transaction has been deposited — which is the provider's fact and not ours — and
 * *"אל תקבע `CancelOnly` כפתרון לכל המקרים"* forbids this screen from choosing for the operator.
 *
 * So the confirmation says, in words, that no money moves, and the result repeats it. An operator
 * who also needs to return money takes that act separately, with the provider's state in front of
 * them.
 *
 * ── THE REMAINING FIGURE IS READ, NEVER COMPUTED HERE ─────────────────────────────────────────
 *
 * `platform.billing.credit.remaining` answers it. A screen that subtracted the credits itself
 * would be a second copy of the service's arithmetic, and the copy drifts — this one would drift
 * into offering a credit the server then refuses, after the operator has typed a figure and told
 * a customer.
 */

import { useCallback, useState } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { Modal } from './Modal';
import { ServerRead } from './ServerRead';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { useServerData } from '../api/useServerData';

type Done = { readonly documentUid: string; readonly remainingAfter: string };

export function CreditControl({
  tenantUid,
  documentUid,
  onIssued,
}: {
  readonly tenantUid: string;
  readonly documentUid: string;
  /** Re-read the documents, so the new credit appears in the list beside its original. */
  readonly onIssued: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  /*
   * THE CONTROL IS A BUTTON UNTIL IT IS PRESSED, and the read happens only then. A documents table
   * renders many rows; reading the creditable balance for every one of them on load would be a
   * request per row for a figure almost none of them will be asked about.
   */
  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        זיכוי
      </button>
    );
  }

  return (
    <CreditPanel
      tenantUid={tenantUid}
      documentUid={documentUid}
      onIssued={onIssued}
      onClose={() => setOpen(false)}
    />
  );
}

function CreditPanel({
  tenantUid,
  documentUid,
  onIssued,
  onClose,
}: {
  readonly tenantUid: string;
  readonly documentUid: string;
  readonly onIssued: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const read = useCallback(
    () =>
      callOperation('platform.billing.credit.remaining', {
        params: { tenant_uid: tenantUid, document_uid: documentUid },
      }),
    [tenantUid, documentUid],
  );
  const creditable = useServerData(read);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  /* A FIXED-POINT FIGURE, never a number. The server compares it against a database constraint. */
  const valid = /^\d+(\.\d{1,2})?$/u.test(amount.trim()) && Number(amount) > 0;

  async function issue(): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      const result = await callOperation('platform.billing.credit.issue', {
        params: { tenant_uid: tenantUid, document_uid: documentUid },
        body: { amountTotal: amount.trim(), reason: reason.trim() },
      });
      setDone({ documentUid: result.documentUid, remainingAfter: result.remainingAfter });
      onIssued();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
      setConfirming(false);
    }
  }

  return (
    <div data-area="credit">
      {failure === null ? null : <ActionFailureNotice failure={failure} />}
      {done === null ? null : (
        <p className="notice__body" data-area="credit-issued">
          {`הזיכוי נרשם. נותר לזיכוי על המסמך הזה: ${done.remainingAfter}. `}
          {/*
            REPEATED AFTER THE FACT, because this is the sentence an operator is most likely to
            read as "handled". *"אין לראות בזיכוי הוכחת תשלום."*
          */}
          לא הוחזר כסף ללקוח — החזר לכרטיס הוא פעולה נפרדת מול הסליקה.
        </p>
      )}

      <ServerRead result={creditable.result} whatHe="את היתרה לזיכוי" skeletonRows={1}>
        {(data) => (
          <div className="field-group">
            <p className="field__hint">
              {`נותר לזיכוי: ${data.remaining} ${data.currencyCode}. זיכוי מלא או חלקי, עד הסכום הזה.`}
            </p>
            <label className="field">
              <span className="field__label">סכום הזיכוי כולל מע״מ</span>
              <input
                className="field__input"
                name={`credit-amount-${documentUid}`}
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.currentTarget.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">סיבת הזיכוי</span>
              <input
                className="field__input"
                name={`credit-reason-${documentUid}`}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
              />
              <span className="field__hint">נרשמת ביומן יחד עם מי שביצע.</span>
            </label>
            <div className="actions-row">
              <button
                type="button"
                className="btn btn--primary"
                disabled={!valid || reason.trim() === ''}
                onClick={() => setConfirming(true)}
              >
                רישום הזיכוי
              </button>
              <button type="button" className="btn" onClick={onClose}>
                ביטול
              </button>
            </div>
          </div>
        )}
      </ServerRead>

      {!confirming ? null : (
        <Modal
          intentHe="רישום זיכוי"
          /*
           * 09 §2.2 — and on this screen the sentence that matters is what does NOT happen. An
           * operator agreeing a credit with a customer is exactly the person who might read it as
           * money going back.
           */
          blastRadiusHe={
            `יופק מסמך זיכוי על ${amount} ₪ כולל מע״מ, מקושר למסמך המקורי, ויקטין את החוב בסכום הזה. ` +
            'לא מוחזר כסף ללקוח ולא מבוטלת עסקה: החזר לכרטיס או ביטול עסקה הם פעולה נפרדת מול הסליקה. ' +
            'הפעולה אינה מסמנת את החוב כשולם ואינה סוגרת מחזור גבייה.'
          }
          fields={0}
          irreversible
          confirmLabelHe={sending ? 'רושם…' : 'אישור הזיכוי'}
          onConfirm={() => void issue()}
          onClose={() => setConfirming(false)}
        >
          <p className="form__note">{`הסיבה שתירשם: ${reason.trim()}`}</p>
        </Modal>
      )}
    </div>
  );
}
